import { useEffect, useState } from 'react';
import { TITLE_COLOR_PALETTE, TITLE_EDIT_PRICE } from '@/data/titleColorPalette';
import { useLocale } from '@/context/LocaleContext';
import type { TitleRow } from '@/hooks/useTitleManager';

interface EditTitleModalProps {
  open: boolean;
  row: TitleRow | null;
  balance: number | null;
  balanceLoading: boolean;
  confirming?: boolean;
  onConfirm: (payload: { title: string; color: string }) => void;
  onClose: () => void;
}

function isDarkHex(hex: string | null | undefined): boolean {
  if (!hex) return false;
  const raw = hex.replace('#', '').trim();
  if (raw.length !== 6) return false;
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return false;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.45;
}

export function EditTitleModal({
  open,
  row,
  balance,
  balanceLoading,
  confirming = false,
  onConfirm,
  onClose,
}: EditTitleModalProps) {
  const { t } = useLocale();
  const [title, setTitle] = useState('');
  const [color, setColor] = useState('#FFFFFF');
  const [inputError, setInputError] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    setTitle(row.title);
    setColor(row.color_hex || '#FFFFFF');
    setInputError(false);
  }, [open, row]);

if (!open || !row) return null;

  const price = TITLE_EDIT_PRICE;
  const canAfford = balance !== null && balance >= price;
  const trimmed = title.trim();
  const headerTitle = trimmed || row.title;
  const canConfirm = !balanceLoading && canAfford && !confirming && trimmed.length > 0;

  const handleSubmit = () => {
    if (!trimmed) {
      setInputError(true);
      window.setTimeout(() => setInputError(false), 2000);
      return;
    }
    if (!canConfirm) return;
    onConfirm({ title: trimmed, color });
  };

  return (
    <div
      className="reset-spin-modal silk-confirm-modal tm-edit-modal silk-modal--aaa"
      role="dialog"
      aria-modal

    >
      <div className="reset-spin-modal-content tm-edit-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="reset-spin-modal-header tm-edit-modal-header">
          <h3>
            <span className="tm-edit-header-label">{t('tm_edit_title_modal_title')}</span>
            <span className="tm-edit-header-sep" aria-hidden>
              |
            </span>
            <span
              className={`tm-edit-header-title${isDarkHex(color) ? ' title-text--dark' : ''}`}
              style={{ color }}
            >
              {headerTitle}
            </span>
          </h3>
          <button type="button" className="battlepass-exp-modal-close tm-modal-close" aria-label={t('silk_confirm_close')} disabled={confirming} onClick={onClose}>×</button>
        </div>

        <div className="reset-spin-modal-body tm-edit-modal-body">
          <div className="tm-edit-field">
            <label className="tm-edit-label" htmlFor="tm-edit-title-input">
              {t('tm_buy_title_label')}
            </label>
            <input
              id="tm-edit-title-input"
              type="text"
              className={`tm-edit-input${inputError ? ' tm-input-error' : ''}${
                isDarkHex(color) ? ' title-text--dark' : ''
              }`}
              maxLength={64}
              value={title}
              style={{ color }}
              placeholder={t('tm_enter_title')}
              disabled={confirming}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="tm-edit-palette" role="listbox" aria-label={t('tm_price_color')}>
            {TITLE_COLOR_PALETTE.map((c) => (
              <button
                key={c.hex}
                type="button"
                className={`tm-edit-color${color === c.hex ? ' selected' : ''}`}
                style={{ backgroundColor: c.hex }}
                title={c.label}
                disabled={confirming}
                aria-label={c.label}
                aria-selected={color === c.hex}
                onClick={() => setColor(c.hex)}
              />
            ))}
          </div>

          <div className="tm-edit-meta">
            <p className="tm-edit-cost">
              {t('silk_confirm_cost', { amount: price.toLocaleString() })}
            </p>
            <p className="tm-edit-balance">
              {balanceLoading ? (
                <span className="silk-confirm-skeleton-line silk-confirm-skeleton-line--medium" />
              ) : balance !== null ? (
                t('silk_confirm_balance', { amount: balance.toLocaleString() })
              ) : (
                <span className="silk-confirm-skeleton-line silk-confirm-skeleton-line--medium" />
              )}
            </p>
            {!balanceLoading && balance !== null && !canAfford ? (
              <p className="tm-edit-warning">{t('tm_buy_not_enough')}</p>
            ) : null}
          </div>
        </div>

        <div className="reset-spin-modal-footer tm-edit-modal-footer">
          <button
            type="button"
            className="reset-spin-btn reset-spin-btn-cancel"
            disabled={confirming}
            onClick={onClose}
          >
            {t('tm_buy_cancel')}
          </button>
          <button
            type="button"
            className="reset-spin-btn reset-spin-btn-confirm"
            disabled={!canConfirm}
            onClick={handleSubmit}
          >
            {confirming ? t('tm_processing') : t('tm_edit')}
          </button>
        </div>
      </div>
    </div>
  );
}
